import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { waLink } from "../data/site";
import { useLang, CHIPS_EN, CHIPS_HI } from "../i18n";
import Logo from "./Logo";

export default function ChatWidget() {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const chips = lang === "hi" ? CHIPS_HI : CHIPS_EN;

  const send = (text) => {
    const v = (text ?? msg).trim();
    if (!v) return;
    window.open(waLink(`Hello Vikash ji! ${v}`), "_blank", "noopener");
    setMsg("");
    setOpen(false);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-[65] bottom-[136px] md:bottom-24 left-4 md:left-6 w-[calc(100vw-2rem)] max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
            data-testid="chat-panel"
          >
            <div className="bg-navy px-4 py-3.5 flex items-center gap-3 grain relative">
              <div className="bg-white rounded-lg p-1"><Logo compact /></div>
              <div className="grow">
                <p className="text-white font-heading font-bold text-sm leading-tight">Blue City Taxi</p>
                <p className="text-[10px] text-wa font-bold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-wa inline-block" /> {t("chatOnline")}
                </p>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close-btn" className="text-white/60 hover:text-gold p-1">
                <X size={17} />
              </button>
            </div>
            <div className="p-4 bg-royal-50/50 space-y-3 max-h-56 overflow-y-auto" data-lenis-prevent>
              <div className="bg-white rounded-2xl rounded-tl-sm border border-slate-100 shadow-sm px-4 py-3 text-[13px] text-slate-700 max-w-[85%]">
                {t("chatHi")}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {chips.map((c, i) => (
                  <button
                    key={c}
                    onClick={() => send(c)}
                    data-testid={`chat-chip-${i}`}
                    className="text-[11px] font-bold text-royal bg-white border border-royal/25 rounded-full px-3 py-1.5 hover:bg-royal hover:text-white transition-colors"
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-3 border-t border-slate-100 flex gap-2 bg-white"
            >
              <input
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder={t("chatPh")}
                data-testid="chat-input"
                className="grow rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-royal/40 focus:border-royal"
              />
              <button type="submit" aria-label="Send on WhatsApp" data-testid="chat-send-btn" className="shrink-0 w-11 h-11 rounded-xl bg-wa text-white flex items-center justify-center hover:brightness-95 transition">
                <Send size={17} />
              </button>
            </form>
            <p className="pb-2.5 text-center text-[10px] text-slate-400 bg-white flex items-center justify-center gap-1">
              {t("chatWa")} <FaWhatsapp size={11} className="text-wa" />
            </p>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close chat" : "Open chat"}
        data-testid="chat-widget-btn"
        className="fixed z-[65] bottom-20 md:bottom-6 left-4 md:left-6 w-14 h-14 rounded-full bg-royal text-white flex items-center justify-center shadow-xl hover:scale-105 hover:bg-royal-700 transition-all"
      >
        {open ? <X size={22} /> : <MessageCircle size={24} />}
      </button>
    </>
  );
}
